import React, { useEffect, useState } from 'react';
import { Box, ToggleButton, ToggleButtonGroup, ToggleButtonGroupProps } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { theme } from '../../constants/theme';

interface SegmentedControlOption {
    value: string | number;
    label: React.ReactNode;
}

interface SegmentedControlProps extends Omit<ToggleButtonGroupProps, 'value' | 'onChange'> {
    options: SegmentedControlOption[];
    defaultValue?: string | number;
    onChange?: (value: any) => void;
    initialValue?: string | number;
}

const SegmentedControl: React.FC<SegmentedControlProps> = ({
    options,
    defaultValue,
    onClick,
    exclusive = true,
    onChange,
    initialValue,
    ...props
}) => {
    const {
        themeStyle,
        device: { isMobile },
    } = useThemeContext();
    const [selected, setSelected] = useState<string | number | null>();
    const handleSelection = (event: React.MouseEvent<HTMLElement>, newValue: string | number | null) => {
        const updatedValue = newValue === selected ? null : newValue;
        setSelected(updatedValue);
        onChange && onChange(newValue);
    };

    useEffect(() => {
        if (initialValue) setSelected(initialValue);
    }, [initialValue]);

    if (isMobile) {
        return (
            <Box
                sx={{
                    overflowX: 'auto',
                    '&::-webkit-scrollbar': {
                        display: 'none', // Hide scrollbar for webkit browsers
                    },
                    msOverflowStyle: 'none', // Hide scrollbar for IE/Edge
                    scrollbarWidth: 'none', // Hide scrollbar for Firefox
                    // Ensure smooth scrolling on mobile
                    WebkitOverflowScrolling: 'touch',
                }}>
                <ToggleButtonGroup
                    value={selected}
                    exclusive={exclusive}
                    onChange={handleSelection}
                    sx={{
                        // Prevent wrapping and ensure horizontal layout
                        flexWrap: 'nowrap',
                    }}
                    {...props}>
                    {options.map(option => (
                        <ToggleButton
                            key={option.value}
                            value={option.value}
                            sx={{
                                typography: 'subtitle1', // Typography style (e.g., subtitle1, body1)
                                color: theme[themeStyle].tag_white, // Text color
                                backgroundColor: theme[themeStyle].secondaryBg, // Background color 222222
                                border: `0.5px solid ${theme[themeStyle].grey}`,
                                '&:hover': {
                                    backgroundColor: theme[themeStyle].deep_grey_lighter, // Hover effect
                                    color: theme[themeStyle].tag_white,
                                },
                                '&.Mui-selected': {
                                    color: theme[themeStyle].black, // Text color for selected state
                                    backgroundColor: theme[themeStyle].primary_orange, // Background color for selected state
                                    '&:hover': {
                                        color: theme[themeStyle].black, // Text color for selected state
                                        backgroundColor: theme[themeStyle].primary_orange, // Background color for selected state
                                    },
                                },
                                textTransform: 'capitalize',
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                '&:not(:first-of-type)': {
                                    borderLeft: `1px solid ${theme[themeStyle].deep_grey}`, // Custom border-left style
                                },
                                // Ensure buttons don't shrink on small screens
                                flexShrink: 0,
                                whiteSpace: 'nowrap',
                            }}>
                            {option.label}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Box>
        );
    }

    return (
        <ToggleButtonGroup value={selected} exclusive={exclusive} onChange={handleSelection} {...props}>
            {options.map(option => (
                <ToggleButton
                    key={option.value}
                    value={option.value}
                    sx={{
                        typography: 'subtitle1', // Typography style (e.g., subtitle1, body1)
                        color: theme[themeStyle].tag_white, // Text color
                        backgroundColor: theme[themeStyle].secondaryBg, // Background color 222222
                        border: `0.5px solid ${theme[themeStyle].grey}`,
                        '&:hover': {
                            backgroundColor: theme[themeStyle].deep_grey_lighter, // Hover effect
                            color: theme[themeStyle].tag_white,
                        },
                        '&.Mui-selected': {
                            color: theme[themeStyle].black, // Text color for selected state
                            backgroundColor: theme[themeStyle].primary_orange, // Background color for selected state
                            '&:hover': {
                                color: theme[themeStyle].black, // Text color for selected state
                                backgroundColor: theme[themeStyle].primary_orange, // Background color for selected state
                            },
                        },
                        textTransform: 'capitalize',
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        '&:not(:first-of-type)': {
                            borderLeft: `1px solid ${theme[themeStyle].deep_grey}`, // Custom border-left style
                        },
                    }}>
                    {option.label}
                </ToggleButton>
            ))}
        </ToggleButtonGroup>
    );
};

export default SegmentedControl;
